import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { getProjectAtomFeedUrl } from '../atomFeed';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from '../i18n/messages';

const GENERATE_URL_TIMEOUT_MS = 10000;
const CLIPBOARD_TIMEOUT_MS = 5000;
const GENERIC_COPY_ERROR = msg({ id: 'atomFeed.copyFailure', message: 'Unable to copy RSS/Atom URL right now. Please try again.' });
const DEFAULT_BLOCKED_MESSAGE = msg({
    id: 'atomFeed.paidPlanRequired',
    message: 'RSS/Atom feeds are included with paid plans.',
});

interface UseCopyAtomFeedUrlOptions {
    canCopy?: boolean;
    blockedMessage?: LocalizedMessage;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            reject(new Error(timeoutMessage));
        }, timeoutMs);

        promise
            .then((value) => {
                clearTimeout(timeoutId);
                resolve(value);
            })
            .catch((error) => {
                clearTimeout(timeoutId);
                reject(error);
            });
    });
}

async function writeTextToClipboardWeb(text: string): Promise<void> {
    if (typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';
        textarea.style.top = '0';
        textarea.style.left = '0';

        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);

        if (copied) {
            return;
        }
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    throw new Error('Clipboard write failed. Please allow clipboard access and try again.');
}

function normalizeCopyError(error: any): LocalizedMessage {
    const message = error?.message ? String(error.message) : '';
    const normalized = message.toLowerCase();

    if (normalized.includes('sign in')) {
        return msg({ id: 'atomFeed.signInRequired', message: 'Please sign in to copy the RSS/Atom URL.' });
    }
    if (normalized.includes('session expired')) {
        return msg({ id: 'atomFeed.sessionExpired', message: 'Your session expired. Please sign in again.' });
    }
    if (normalized.includes('clipboard')) {
        return msg({ id: 'atomFeed.clipboardFailure', message: 'Clipboard access failed. Please allow clipboard access and try again.' });
    }
    if (normalized.includes('timed out')) {
        return msg({ id: 'atomFeed.copyTimeout', message: 'Copying the RSS/Atom URL timed out. Please try again.' });
    }

    return GENERIC_COPY_ERROR;
}

export function useCopyAtomFeedUrl(options: UseCopyAtomFeedUrlOptions = {}) {
    const { canCopy = true, blockedMessage = DEFAULT_BLOCKED_MESSAGE } = options;
    const [isCopying, setIsCopying] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState<LocalizedMessage | null>(null);
    const [notice, setNotice] = useState<LocalizedMessage | null>(null);
    const cachedUrlRef = useRef<{ projectId: string; url: string } | null>(null);
    const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (canCopy) {
            setNotice(null);
        }
    }, [canCopy]);

    const generateAtomFeedUrl = useCallback(async (projectId: string): Promise<string> => {
        const atomFeedUrl = await withTimeout(
            getProjectAtomFeedUrl(projectId),
            GENERATE_URL_TIMEOUT_MS,
            'Generating RSS/Atom URL timed out. Please try again.'
        );

        cachedUrlRef.current = { projectId, url: atomFeedUrl };
        return atomFeedUrl;
    }, []);

    const copyAtomFeedUrl = useCallback(async (projectId: string): Promise<void> => {
        if (!projectId || isCopying) {
            return;
        }

        if (!canCopy) {
            setCopied(false);
            setError(null);
            setNotice(blockedMessage);
            return;
        }

        setIsCopying(true);
        setError(null);
        setNotice(null);

        try {
            const cached = cachedUrlRef.current;
            const atomFeedUrl = cached?.projectId === projectId
                ? cached.url
                : await generateAtomFeedUrl(projectId);

            if (Platform.OS === 'web') {
                await withTimeout(
                    writeTextToClipboardWeb(atomFeedUrl),
                    CLIPBOARD_TIMEOUT_MS,
                    'Clipboard write timed out. Please try again.'
                );
            } else {
                const Clipboard = require('expo-clipboard');
                await withTimeout(
                    Clipboard.setStringAsync(atomFeedUrl),
                    CLIPBOARD_TIMEOUT_MS,
                    'Clipboard write timed out. Please try again.'
                );
            }

            setCopied(true);
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
            resetTimeoutRef.current = setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch (err: any) {
            console.error('[AtomFeed] Could not copy feed URL:', err);
            setCopied(false);
            setNotice(null);
            setError(normalizeCopyError(err));
        } finally {
            setIsCopying(false);
        }
    }, [blockedMessage, canCopy, generateAtomFeedUrl, isCopying]);

    return {
        copyAtomFeedUrl,
        isCopying,
        copied,
        error,
        notice,
    };
}
